import { isItemReviewable } from "@memorang/helpers/item";
import type { BaseItemSchema } from "@memorang/types";
import { useTranslation } from "react-i18next";
import Button from "../Button";
import { SessionReportAnswerRow } from "./SessionReportAnswerRow";
import { SessionReportCard } from "./SessionReportCard";

export const MAX_ANSWER_ROWS = 3;

type Props = {
	sessionItems: BaseItemSchema[];
	isMarkable?: boolean;
	hideSkills?: boolean;
	onOpenReviewAnswers: (items: BaseItemSchema[]) => void;
	onViewAll: () => void;
	isReviewLoading?: boolean;
};

export const SessionReportAnswerCard = ({
	sessionItems,
	isMarkable = false,
	hideSkills = false,
	onOpenReviewAnswers,
	onViewAll,
	isReviewLoading = false,
}: Props) => {
	const { t } = useTranslation();

	const reviewItems = sessionItems.filter((item) => isItemReviewable(item));
	const visibleItems = reviewItems.slice(0, MAX_ANSWER_ROWS);
	const showViewAll = reviewItems.length > MAX_ANSWER_ROWS;

	return (
		<SessionReportCard.Frame>
			<SessionReportCard.Header
				icon="text-box-check-outline"
				title={t("report.answerCardTitle")}
				subtitle={t("report.answerCardSubtitle", {
					count: reviewItems.length,
				})}
			/>
			<SessionReportCard.Body paddingHorizontal={16}>
				{visibleItems.map((item, index) => (
					<SessionReportAnswerRow
						key={item.id}
						item={item}
						isMarkable={isMarkable}
						hideSkills={hideSkills}
						hideDivider={index === visibleItems.length - 1}
					/>
				))}
			</SessionReportCard.Body>
			{reviewItems.length > 0 ? (
				<SessionReportCard.Footer
					paddingHorizontal={16}
					paddingTop={8}
					paddingBottom={16}
				>
					{showViewAll ? (
						<Button mode="text" onPress={onViewAll} disabled={isReviewLoading}>
							{t("common.viewAll")}
						</Button>
					) : null}
					<Button
						mode="contained"
						onPress={() => onOpenReviewAnswers(reviewItems)}
						loading={isReviewLoading}
						disabled={isReviewLoading}
					>
						{t("report.actions.reviewAnswers")}
					</Button>
				</SessionReportCard.Footer>
			) : null}
		</SessionReportCard.Frame>
	);
};
